import { lazy as reactLazy, type ComponentType } from 'react';

/**
 * RECARGA SOLA CUANDO SUBIMOS UNA VERSIÓN NUEVA.
 *
 * Cada deploy publica los chunks con un hash nuevo y borra los viejos. Quien tenía el CRM
 * abierto sigue con el `index.html` anterior en memoria, así que al entrar a una página
 * lazy pide un `.js` que ya no existe. Vercel hace fallback de SPA y le devuelve el
 * `index.html` con `content-type: text/html` — HTTP 200, pero no es un módulo. El import
 * dinámico revienta y el agente ve "Unexpected Application Error!".
 *
 * (28/08/2026) Le pasó a Katherine Alanis desde el Safari de un iPhone: la pantalla en
 * negro decía `'text/html' is not a valid JavaScript MIME type` y ella lo reportó como un
 * bug de la búsqueda de clientes. El listener de `vite:preloadError` que ya estaba en
 * `main.tsx` NO la cubrió: solo salta cuando falla el preload de Vite, y acá el que falló
 * fue el `import()` del `lazy`, que va directo al ErrorBoundary del router.
 *
 * Por eso hay DOS capas y las dos comparten el mismo guard: el listener de preload, y este
 * wrapper alrededor de cada página lazy.
 */

/** Ventana del guard. Si la recarga no alcanzó, no se reintenta: se muestra el error. */
const VENTANA_MS = 10_000;
const KEY = 'vite-preload-reload-ts';

/**
 * Los mensajes con los que cada navegador cuenta lo mismo. Se compara en minúsculas.
 *
 * ⛔ `'text/html' is not a valid javascript mime type` es el de Safari/iOS y es el que
 * faltaba: no dice "failed to fetch" en ningún lado, porque el archivo SÍ se bajó — lo que
 * falla es que sea HTML. Un filtro que busque solo "failed to fetch" lo deja pasar.
 */
const SENIALES = [
  'failed to fetch dynamically imported module',   // Chrome / Edge
  'error loading dynamically imported module',     // Firefox
  'is not a valid javascript mime type',           // Safari / iOS  ← el de Katherine
  'importing a module script failed',              // Safari (variante)
  'expected a javascript module script',           // Chrome, cuando llega text/html
  'unable to preload css',                         // Vite, hoja de estilo del chunk
  'dynamically imported module',                   // red de seguridad
];

/** ¿Este error es "el chunk ya no está", y no un bug de la página? */
export function esErrorDeChunk(err: unknown): boolean {
  const msg = (err instanceof Error ? err.message : String(err ?? '')).toLowerCase();
  if (!msg) return false;
  return SENIALES.some((s) => msg.includes(s));
}

/**
 * Recarga una sola vez por ventana. Devuelve false si ya se había recargado recién, y ahí
 * el error tiene que propagarse: sin este freno, un fallo que la recarga no arregla (sin
 * señal en el ascensor, un chunk que de verdad falta) deja al agente en un loop de
 * recargas del que no puede salir ni para cerrar sesión.
 */
export function recargarUnaVez(): boolean {
  try {
    const last = Number(sessionStorage.getItem(KEY) || '0');
    if (Date.now() - last < VENTANA_MS) return false;
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    // Safari en privado tira al escribir. Sin guard preferimos recargar igual: el caso
    // común es el deploy, y ahí la recarga es lo que lo arregla.
  }
  window.location.reload();
  return true;
}

/**
 * `React.lazy` que, si el chunk no está, recarga en vez de mostrar la pantalla de error.
 * Se usa igual que `lazy`. Para cualquier otro error (uno de verdad, en el módulo) se
 * comporta como `lazy` y deja que el error salga.
 */
export function lazyConRecarga<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return reactLazy<T>(() => factory().catch((err): Promise<{ default: T }> => {
    if (esErrorDeChunk(err) && recargarUnaVez()) {
      // La recarga ya está pedida; devolvemos una promesa que nunca resuelve para que la
      // pantalla se quede en el Suspense y no parpadee el error justo antes de irse.
      return new Promise<{ default: T }>(() => {});
    }
    throw err;
  }));
}
